/**
 * O contrato que todo bundle dos cinco portais precisa cumprir (issue #996, etapa 1).
 *
 * ## O defeito que isto fecha
 *
 * Em 2026-09-09 `bbc.nexxus.dev.br` — um subdomínio de STAGING — foi ligado ao projeto
 * Cloudflare Pages de PRODUÇÃO. O navegador recebeu o bundle de produção e autenticou
 * em `https://api.nexxuscloud.com.br`. Consertar o vínculo projeto↔domínio no backend é
 * das etapas seguintes da issue. O que se conserta aqui é o outro lado: um bundle sem
 * configuração NÃO FALHAVA. `services/api.ts` emitia um `console.warn` e seguia com a
 * URL de produção; `store/portalStore.ts` e `config/apps.ts` carregavam listas de hosts
 * de produção. Falha ABERTA — o pior desfecho possível, porque parece funcionar.
 *
 * A troca é deliberada: hoje um build mal configurado sobe e fala com produção em
 * silêncio; depois desta régua ele não sobe. O segundo erro é melhor, e só é aceitável
 * porque o operador sabe o que configurar — ver `frontend-apps/CLAUDE.md`.
 *
 * ## Onde esta régua roda
 *
 * Nas DUAS pontas, e é de propósito que seja a mesma função:
 *
 * - **build**: `build/vitePluginDeploymentContract.ts` a chama com o `config.env` já
 *   resolvido pelo Vite — exatamente o objeto que vira `import.meta.env` no bundle — e
 *   derruba `vite build`/`vite dev` quando ela recusa.
 * - **runtime**: `config/activeDeploymentContract.ts` a chama com `import.meta.env`.
 *
 * Duas cópias divergiriam caladas, com o build aprovando o que o runtime recusa.
 *
 * ## Por que quatro variáveis, e não três
 *
 * A issue nomeia três obrigatórias (API, aplicação e ambiente). A quarta,
 * `VITE_PORTAL_BASE_DOMAINS`, é o que substitui as listas literais de
 * `config/apps.ts`: sem ela, "a URL do portal `rs`" não teria resposta que não fosse um
 * literal de produção — que é justamente o que a issue manda remover.
 */

/** As cinco aplicações do monorepo. `VITE_APP_TYPE` só aceita estes valores. */
export const APPLICATIONS = ['adm', 'cp', 'rs', 'ec', 'hub'] as const
export type Application = (typeof APPLICATIONS)[number]

/**
 * Conjunto FECHADO de ambientes. "Inferir ambiente por `DEBUG`, `Host`, sufixo
 * arbitrário ou nome do projeto" está no "fora de escopo" da issue: `prod`, `preview` e
 * `homolog` são recusados, não traduzidos por parecença.
 */
export const ENVIRONMENTS = ['development', 'staging', 'production'] as const
export type Environment = (typeof ENVIRONMENTS)[number]

/** As variáveis sem as quais o build não sai. Ordem = ordem em que aparecem no erro. */
export const REQUIRED_ENV_VARS = [
  'VITE_API_URL',
  'VITE_APP_TYPE',
  'VITE_ENV',
  'VITE_PORTAL_BASE_DOMAINS',
] as const

/** Prefixo dos overrides opcionais: `VITE_PORTAL_URL_ADM`, `…_CP`, `…_RS`, `…_EC`, `…_HUB`. */
export const PORTAL_URL_ENV_PREFIX = 'VITE_PORTAL_URL_'

export const portalUrlEnvVar = (app: Application): string =>
  `${PORTAL_URL_ENV_PREFIX}${app.toUpperCase()}`

export interface DeploymentContract {
  /** Qual dos cinco portais este bundle é. */
  readonly application: Application
  /** Ambiente que este bundle serve. */
  readonly environment: Environment
  /** Base de toda chamada à API, sem barra final. */
  readonly apiBaseUrl: string
  /**
   * Domínios sob os quais os portais desta plataforma vivem, o primeiro sendo o
   * canônico. É lista porque a plataforma tem mais de um hoje (`nexxuscloud.com.br` e
   * `nebula.viaiv.com.br`) — presumir um só tiraria os hosts do outro da detecção.
   */
  readonly portalBaseDomains: readonly string[]
  /**
   * URL explícita de um portal, quando o ambiente não segue o padrão de subdomínio.
   * Vence sobre o domínio-base.
   */
  readonly portalUrls: Readonly<Partial<Record<Application, string>>>
}

/** O JSON público publicado em `/deployment-manifest.json`. */
export interface DeploymentManifest {
  readonly application: Application
  readonly environment: Environment
  readonly apiBaseUrl: string
  readonly commitSha: string
}

/**
 * Recusa do contrato, com a lista INTEIRA de problemas.
 *
 * Um problema por rodada faria o operador descobrir as quatro variáveis em quatro
 * builds do Cloudflare Pages — e cada rodada dessas é um deploy quebrado a mais.
 */
export class DeploymentContractError extends Error {
  readonly problems: readonly string[]

  constructor(problems: readonly string[]) {
    super(
      [
        'Contrato de deploy inválido: o build não pode continuar.',
        ...problems.map((p) => `  - ${p}`),
        '',
        'Estas variáveis são obrigatórias em TODO build (Cloudflare Pages, CI, Docker):',
        `  ${REQUIRED_ENV_VARS.join(', ')}`,
        'Ver `frontend-apps/CLAUDE.md` (seção "Contrato de deploy") para os valores de cada projeto.',
      ].join('\n'),
    )
    this.name = 'DeploymentContractError'
    this.problems = problems
  }
}

/** Fonte de leitura: `import.meta.env` do Vite, ou o `config.env` resolvido no build. */
export type DeploymentContractEnv = Record<string, unknown> | undefined

/**
 * `typeof === 'string'` e não `String(valor)`: `ImportMetaEnv` tipa os campos com index
 * signature `any`, então um número ou objeto chegaria aqui sem o compilador reclamar.
 * Coagir para string faria `42` virar `"42"` e passar por validações de formato.
 *
 * Espaços são aparados e `''` conta como AUSENTE: o painel do Pages grava variável
 * vazia com a mesma facilidade com que a omite, e `''` passa por qualquer checagem de
 * `undefined`.
 */
const textoOuVazio = (valor: unknown): string => (typeof valor === 'string' ? valor.trim() : '')

/**
 * Hostname puro: sem esquema, sem porta, sem caminho, sem credencial. `localhost` casa
 * (é o domínio-base de desenvolvimento).
 */
const HOSTNAME = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/

/**
 * Origem absoluta http(s). Devolve o texto normalizado (sem barra final) junto com o
 * protocolo JÁ PARSEADO, ou `null`.
 *
 * `new URL` sozinho aceita `ftp://`, `mailto:` e caminhos relativos com base — daí a
 * conferência explícita do protocolo e do host.
 *
 * O protocolo sai daqui, e não de um `startsWith('https://')` no texto, porque o texto
 * cru não é normalizado: `HTTPS://api.exemplo.com.br` é uma URL https perfeitamente
 * válida (`url.protocol === 'https:'`) que a comparação por prefixo recusaria — com uma
 * mensagem dizendo ao operador que a URL dele não é https, quando é.
 */
const origemAbsoluta = (valor: string): { texto: string; protocolo: string } | null => {
  let url: URL
  try {
    url = new URL(valor)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
  if (!url.hostname) return null
  return { texto: valor.replace(/\/+$/, ''), protocolo: url.protocol }
}

/**
 * Lê e valida o contrato. Lança `DeploymentContractError` com TODOS os problemas.
 *
 * Não existe caminho de saída que devolva um contrato parcial ou um default: é a única
 * garantia de que "não configurado" nunca vira "produção".
 */
export const readDeploymentContract = (env: DeploymentContractEnv): DeploymentContract => {
  const problems: string[] = []
  const ler = (nome: string) => textoOuVazio(env?.[nome])

  // --- aplicação -----------------------------------------------------------------
  const appDeclarada = ler('VITE_APP_TYPE')
  let application: Application | null = null
  if (!appDeclarada) {
    problems.push('VITE_APP_TYPE ausente — declare qual portal este bundle é.')
  } else if (!(APPLICATIONS as readonly string[]).includes(appDeclarada)) {
    problems.push(
      `VITE_APP_TYPE inválida (${appDeclarada}) — use uma de: ${APPLICATIONS.join(', ')}.`,
    )
  } else {
    application = appDeclarada as Application
  }

  // --- ambiente ------------------------------------------------------------------
  const ambienteDeclarado = ler('VITE_ENV')
  let environment: Environment | null = null
  if (!ambienteDeclarado) {
    problems.push('VITE_ENV ausente — declare o ambiente que este bundle serve.')
  } else if (!(ENVIRONMENTS as readonly string[]).includes(ambienteDeclarado)) {
    problems.push(
      `VITE_ENV inválido (${ambienteDeclarado}) — use um de: ${ENVIRONMENTS.join(', ')}.`,
    )
  } else {
    environment = ambienteDeclarado as Environment
  }

  // --- API -----------------------------------------------------------------------
  const apiDeclarada = ler('VITE_API_URL')
  let apiBaseUrl = ''
  if (!apiDeclarada) {
    problems.push('VITE_API_URL ausente — declare a API que este bundle deve chamar.')
  } else {
    const normalizada = origemAbsoluta(apiDeclarada)
    if (!normalizada) {
      problems.push(
        `VITE_API_URL inválida (${apiDeclarada}) — precisa ser URL absoluta http(s), ex.: https://api.exemplo.com.br`,
      )
    } else if (environment !== 'development' && normalizada.protocolo !== 'https:') {
      // Reescrever `http://` para `https://` era o que `api.ts` fazia. Corrigir a
      // configuração do operador em silêncio esconde o erro em vez de mostrá-lo — e o
      // erro que interessa aqui é exatamente "esta URL não é a que eu penso que é".
      problems.push(`VITE_API_URL precisa ser https fora de development (${normalizada.texto}).`)
    } else {
      apiBaseUrl = normalizada.texto
    }
  }

  // --- domínios-base dos portais ---------------------------------------------------
  const dominiosDeclarados = ler('VITE_PORTAL_BASE_DOMAINS')
  let portalBaseDomains: string[] = []
  if (!dominiosDeclarados) {
    problems.push(
      'VITE_PORTAL_BASE_DOMAINS ausente — declare o(s) domínio(s) sob os quais os portais vivem, ' +
        'ex.: exemplo.com.br,outra-zona.exemplo.com',
    )
  } else {
    const itens = dominiosDeclarados.split(',').map((d) => d.trim().toLowerCase())
    const invalidos = itens.filter((d) => !HOSTNAME.test(d))
    if (invalidos.length > 0 || itens.length === 0) {
      problems.push(
        `VITE_PORTAL_BASE_DOMAINS inválida (${invalidos.join(', ') || dominiosDeclarados}) — ` +
          'cada item é um hostname puro, sem esquema, porta ou caminho.',
      )
    } else {
      portalBaseDomains = itens
    }
  }

  // --- overrides por portal (opcionais) --------------------------------------------
  const portalUrls: Partial<Record<Application, string>> = {}
  for (const app of APPLICATIONS) {
    const nome = portalUrlEnvVar(app)
    const declarada = ler(nome)
    if (!declarada) continue
    const normalizada = origemAbsoluta(declarada)
    if (!normalizada) {
      problems.push(`${nome} inválida (${declarada}) — precisa ser URL absoluta http(s).`)
      continue
    }
    portalUrls[app] = normalizada.texto
  }

  if (problems.length > 0) {
    throw new DeploymentContractError(problems)
  }

  return {
    application: application as Application,
    environment: environment as Environment,
    apiBaseUrl,
    portalBaseDomains,
    portalUrls,
  }
}

/**
 * O commit desconhecido vira `'unknown'`, e não campo ausente: quem lê o manifesto
 * (etapa 2 da issue) precisa distinguir "build sem proveniência" de "manifesto de
 * formato antigo".
 */
export const buildDeploymentManifest = (
  contract: DeploymentContract,
  commitSha: string | undefined,
): DeploymentManifest => ({
  application: contract.application,
  environment: contract.environment,
  apiBaseUrl: contract.apiBaseUrl,
  // O manifesto é PÚBLICO — só os quatro campos que a issue especifica. Despejar o
  // ambiente do build aqui vazaria o que ninguém auditou.
  commitSha: (typeof commitSha === 'string' ? commitSha.trim() : '') || 'unknown',
})
